/**
 * Premium-feature availability for a school, read off its own `billing`.
 *
 * This is a UI hint, not the gate: the server resolves entitlement
 * (`server/src/entitlements.ts`, BILLING_SPEC §5.3). A school inside a
 * district is covered by the district contract, which the browser cannot
 * read, so it is treated optimistically — same policy as
 * `aiAnalysisAccess.ts`. A standalone school pays for itself; there is no
 * other org to walk to.
 */

import { Organization } from '../types/user';

export type BillingStatus = NonNullable<Organization['billing']>['status'];

export interface PremiumAccessResult {
  hasPremium: boolean;
  isPending: boolean; // true if the school has not paid yet
  billingStatus: BillingStatus | null;
}

export function getEffectivePremiumAccess(org: Organization): PremiumAccessResult {
  const billingStatus = org.billing?.status ?? null;

  if (org.district_id) {
    return { hasPremium: true, isPending: false, billingStatus };
  }

  const isPremium = org.billing?.plan_type === 'premium';
  const isPending = billingStatus === 'pending';
  const hasPremium =
    isPremium && (billingStatus === 'active' || billingStatus === 'trialing');

  return { hasPremium, isPending, billingStatus };
}
